// src/components/Portfolio.js
import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";

// Projects
const projects = [
    {
    id: 5,
    title: "THE SHERSIAH HOUSE",
    area: "RESIDENTIAL  2500 sq.ft",
    style: "The Modern Minimalist",
    desc: "The combination of walnut laminates with the white family of finishes creates a warm yet balanced contrast — where walnut adds depth and richness, while white introduces freshness and lightness. This pairing sits harmoniously on a grey flooring base, which acts as a neutral anchor, enhancing the sophistication of the palette. Together, the tones bring a timeless and modern appeal — perfect for creating interiors that feel both inviting and contemporary.",
    img: "/images/i/i/5_vdo.mp4",
    images: [
      "/images/i/i/5_0.jpeg",
      "/images/i/i/5_3.jpeg",
      "/images/i/i/5_1.jpeg",
      "/images/i/i/5_2.jpeg",
      "/images/i/i/5_4.jpeg",
      "/images/i/i/5_5.jpeg",
      "/images/i/i/5_6.jpeg",
      "/images/i/i/5_7.jpeg",
      "/images/i/i/5_8.jpeg",
      "/images/i/i/5_9.jpeg",
      "/images/i/i/5_10.jpeg",
    ]
  },
  { id: 1, title: "RODRIGUES HOUSE, PUNE", area: "RESIDENTIAL  2000 sq.ft", style: "The Artistic Bohemian", desc: "Crafted for a creative soul, this home embraces bold colors, eclectic decor, handcrafted elements, and layered textures — celebrating individuality, freedom, and artistic expression.", img: "/images/i/IMG_9029.JPG", images: ["/images/i/IMG_9029.JPG"] },
  { id: 2, title: "CHOPRA'S HOUSE, BANDRA", area: "RESIDENTIAL  1200 sq.ft", style: "The Urban Contemporary", desc: "Tailored for a fast-paced professional lifestyle, this apartment showcases bold accents, sleek finishes, and multi-functional spaces that reflect urban sensibilities and efficiency.", img: "/images/i/int3.jpg", images: ["/images/i/int3.jpg"] },
  { id: 3, title: "SURYAVANSHI HOUSE", area: "RESIDENTIAL  2000 sq.ft", style: "The Modern Minimalist", desc: "For a young couple seeking simplicity and elegance, we created a clutter-free home with clean lines, neutral palettes, and smart storage — blending modern design with functional living.", img: "/images/i/IMG_9031.JPG", images: ["/images/i/IMG_9031.JPG"] },
  { id: 4, title: "MUJAWAR VILLA", area: "RESIDENTIAL  3500 sq.ft", style: "The Nature–Inspired Retreat", desc: "A serene home designed for a nature-loving couple, , natural  light, and open layouts that bring the outdoors in — a perfect blend of calm and character", img: "/images/i/IMG_9032.JPG", images: ["/images/i/IMG_9032.JPG"] },

];

const PALETTE = {
  ivory: "#F7F1E8",
  charcoal: "#593c24",
  tan: "#C3A585",
  tanHover: "#b59473",
  textDark: "#2A2A2A",
  textLight: "#FFFFFF",
  subDark: "#6C6C6C",
  subLight: "#D2D2D2",
  lineLight: "#DCD2C5",
  lineDark: "rgba(255,255,255,0.18)",
};

const serif = "font-[Playfair,serif]";

// Motion variants
const fadeSlideLeft = {
  hidden: { opacity: 0, x: -100 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
}; // slide from left [web:9]

const fadeSlideRight = {
  hidden: { opacity: 0, x: 100 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } },
}; // slide from right [web:9]

// Trigger when ~40% in view, only once
const sectionViewport = { once: true, amount: 0.4 }; // whileInView config [web:9]

// Safe mobile detection memoized
function useIsMobile() {
  return useMemo(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(max-width: 640px)").matches;
  }, []);
}

function isVideo(src) {
  return /\.(mp4|webm|mov|ogg)$/i.test(src);
}

function isImage(src) {
  return /\.(jpe?g|png|gif|webp|avif|svg)$/i.test(src);
}

// Hook to detect if an image is landscape using naturalWidth/Height
function useIsLandscape(src) {
  if (!isImage(src)) return false;
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    let mounted = true;
    const img = new Image();
    img.src = src;
    img.onload = () => {
      if (!mounted) return;
      setIsLandscape(img.naturalWidth > img.naturalHeight);
    };
    return () => { mounted = false; };
  }, [src]);
  return isLandscape;
}

// Panning image that swaps renderer based on orientation
function PanningImage({ src, alt, dark, onClick }) {
  const isLandscape = useIsLandscape(src); // orientation via natural sizes [web:13]

  const baseClass = [
    "relative w-full max-w-[420px] sm:max-w-[420px] lg:max-w-[420px] aspect-[4/5] overflow-hidden",
    dark ? "soft-elev-on-dark" : "soft-elev",
    "soft-hover-pop"
  ].join(" ");

  // Common hover elevation
  const hoverProps = {
    initial: { filter: "drop-shadow(0px 0px 0px rgba(0,0,0,0))" },
    whileHover: {
      filter:
        "drop-shadow(0px 6px 14px rgba(0,0,0,0.20)) drop-shadow(0px 2px 6px rgba(0,0,0,0.10))",
      y: -1
    },
    transition: { duration: 0.22, ease: "easeOut" }
  };

    if (isVideo(src)) {
    return (
      <motion.div className={baseClass} {...hoverProps}>
        <video
          src={src}
          className="w-full h-full object-cover cursor-pointer"
          muted
          loop
          playsInline
          autoPlay
          controls
          onClick={onClick}
        />
      </motion.div>
    );
  }

  // For portrait or unknown ratio, keep <img> object-cover
  if (!isLandscape) {
    return (
      <motion.div className={baseClass} {...hoverProps}>
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover cursor-zoom-in"
          onClick={onClick}
        />
      </motion.div>
    );
  }

  // For landscape, use background-image with Motion keyframes on backgroundPositionX
  return (
    <motion.div
      className={baseClass + " cursor-zoom-in"}
      onClick={onClick}
      style={{
        backgroundImage: `url(${src})`,
        backgroundSize: "cover", // can bump to "110% auto" if edges appear
        backgroundRepeat: "no-repeat",
        backgroundPosition: "0% 50%"
      }}
      {...hoverProps}
      animate={{ backgroundPositionX: ["0%", "100%", "0%"] }} // left→right→left loop [web:9]
      transition={{ duration: 30, ease: "easeInOut", repeat: Infinity }} // calm sweep [web:9]
      aria-label={alt}
      role="img"
    />
  );
}

export default function Portfolio() {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [slides, setSlides] = useState([]);

  const openGallery = (project, startIndex = 0) => {
    const media = [project.img, ...project.images.filter((src) => src !== project.img)];
    const uniqueMedia = Array.from(new Set(media));
    setSlides(uniqueMedia.map((src) => ({ src, type: isVideo(src) ? "video" : "image" })));
    setIndex(startIndex);
    setOpen(true);
  };

  useEffect(() => {
    if (!open || slides.length < 2) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [open, slides.length]);

  useEffect(() => {
    if (!open || slides.length === 0) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key === "ArrowRight") setIndex((prev) => (prev + 1) % slides.length);
      if (event.key === "ArrowLeft") setIndex((prev) => (prev - 1 + slides.length) % slides.length);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, slides.length]);

  return (
    <section className="w-full pt-[65px]" style={{ backgroundColor: PALETTE.ivory }}>
      {/* Header */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
        <h1 className={`${serif} text-center tracking-[0.2em] text-3xl sm:text-5xl`} style={{ color: "rgb(89, 60, 36)" }}>
          INTERIORS
        </h1>
      </div>

      {/* Row 1 (light) */}
      <Band>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
          <Row
            number={1}
            title={projects[0].title}
            price={projects[0].area}
            bullets={[projects[0].style, projects[0].desc]}
            image={projects[0].img}
            imageSide="right"
            dark={false}
            onOpen={() => openGallery(projects[0])}
          />
        </div>
      </Band>

      {/* Row 2 (dark) */}
      <Band dark>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={2}
            title={projects[1].title}
            price={projects[1].area}
            bullets={[projects[1].style, projects[1].desc]}
            image={projects[1].img}
            imageSide="left"
            dark
            onOpen={() => openGallery(projects[1])}
          />
        </div>
      </Band>

      {/* Row 3 (light) */}
      <Band>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={3}
            title={projects[2].title}
            price={projects[2].area}
            bullets={[projects[2].style, projects[2].desc]}
            image={projects[2].img}
            imageSide="right"
            dark={false}
            onOpen={() => openGallery(projects[2])}
          />
        </div>
      </Band>

      {/* Row 4 (dark) */}
      <Band dark>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={4}
            title={projects[3].title}
            price={projects[3].area}
            bullets={[projects[3].style, projects[3].desc]}
            image={projects[3].img}
            imageSide="left"
            dark
            onOpen={() => openGallery(projects[3])}
          />
        </div>
      </Band>

      {/* Row 5 (light) */}
      <Band>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12">
          <Row
            number={5}
            title={projects[4].title}
            price={projects[4].area}
            bullets={[projects[4].style, projects[4].desc]}
            image={projects[4].img}
            imageSide="right"
            dark={false}
            onOpen={() => openGallery(projects[4])}
          />
        </div>
      </Band>

      <ImageSliderModal
        open={open}
        slides={slides}
        index={index}
        onClose={() => setOpen(false)}
        onPrev={() => setIndex((prev) => (prev - 1 + slides.length) % slides.length)}
        onNext={() => setIndex((prev) => (prev + 1) % slides.length)}
        onDotClick={setIndex}
      />
    </section>
  );
}

function ImageSliderModal({ open, slides, index, onClose, onPrev, onNext, onDotClick }) {
  if (!open || slides.length === 0) return null;

  const currentSlide = slides[index];

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 flex items-center justify-center p-2 sm:p-3"
      onClick={onClose}
    >
      <div className="relative w-full max-w-[96vw]" onClick={(event) => event.stopPropagation()}>
        <div className="relative overflow-hidden rounded-md bg-black">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 z-10 h-10 w-10 rounded-full bg-black/55 text-white text-2xl leading-none"
            aria-label="Close slider"
          >
            ×
          </button>

          {currentSlide.type === "video" ? (
            <video
              src={currentSlide.src}
              className="w-full max-h-[88vh] object-contain"
              controls
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              src={currentSlide.src}
              alt={`Slide ${index + 1}`}
              className="w-full max-h-[88vh] object-contain"
            />
          )}

          {slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={onPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/20 text-white text-xl"
                aria-label="Previous slide"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={onNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/20 text-white text-xl"
                aria-label="Next slide"
              >
                ›
              </button>
            </>
          )}
        </div>

        {slides.length > 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {slides.map((_, dotIndex) => (
              <button
                key={dotIndex}
                type="button"
                onClick={() => onDotClick(dotIndex)}
                className={`h-2.5 w-2.5 rounded-full transition ${
                  dotIndex === index ? "bg-white" : "bg-white/40 hover:bg-white/70"
                }`}
                aria-label={`Go to slide ${dotIndex + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Band({ children, dark = false }) {
  return (
    <div style={{ backgroundColor: dark ? PALETTE.charcoal : "transparent", color: dark ? PALETTE.textLight : PALETTE.textDark }}>
      {children}
    </div>
  );
}

function Row({ number, title, price, bullets, image, imageSide = "right", dark = false, onOpen }) {
  const textColor = dark ? PALETTE.textLight : PALETTE.textDark;
  const subColor = dark ? PALETTE.subLight : PALETTE.subDark;
  const lineColor = dark ? PALETTE.lineDark : PALETTE.lineLight;
  const isMobile = useIsMobile(); // mobile handling [web:9]

  // Shared image block with layered shadow + hover pop + panning for landscape
  const Image = (
    <div className="relative flex justify-center md:justify-end">
      {/* Outline frame */}
      <div
        className="absolute -top-4 -right-4 w-[79%] h-[79%] border"
        style={{ borderColor: lineColor }}
      />
      {/* Constrained portrait wrapper with soft shadow */}
      <PanningImage
        src={image}
        alt={title}
        dark={dark}
        onClick={onOpen}
      />
    </div>
  );

  // Text block with inner subgrid for number + content
  const Text = ({ side = "left" }) => {
    const numAlign = side === "right" ? "text-right" : "text-left";

    return (
      <div className="w-full">
        {/* two-col subgrid: [number | content] */}
        <div className="grid items-start gap-4 grid-cols-[90px_minmax(0,1fr)]">
          {/* Number column mirrors side */}
          <motion.div
            className={`${serif} leading-none ${numAlign}`}
            style={{ color: PALETTE.tan, fontSize: "126px", lineHeight: 0.9 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}
            viewport={sectionViewport}
          >
            {number}
          </motion.div>

          {/* Content column */}
          <motion.div
            className="flex flex-col items-start text-left max-w-[560px]"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.12 }}
            viewport={sectionViewport}
          >
            <h2 className={`${serif} md:text-4xl text-2xl`} style={{ color: textColor }}>
              {title}
            </h2>
            <div className="mt-1 h-px w-40" style={{ backgroundColor: lineColor }} />
            <p className="mt-2 md:text-2xl text-base sub" style={{ color: textColor }}>
              {price}
            </p>

            <ul className="mt-4 space-y-3">
              {bullets.map((b, i) => (
                <li key={i} className=" leading-relaxed" style={{ color: subColor }}>
                  • {b}
                </li>
              ))}
            </ul>

            <button
              type="button"
              className="view-more-btn sub mt-6 inline-block px-6 py-2 text-sm tracking-wide"
              style={{ backgroundColor: PALETTE.tan, color: "#1B1B1B" }}
              onMouseEnter={(event) => {
                event.currentTarget.style.color = "#1B1B1B";
                event.currentTarget.style.backgroundColor = PALETTE.tanHover;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.color = "#1B1B1B";
                event.currentTarget.style.backgroundColor = PALETTE.tan;
              }}
              onFocus={(event) => {
                event.currentTarget.style.color = "#1B1B1B";
                event.currentTarget.style.backgroundColor = PALETTE.tanHover;
              }}
              onBlur={(event) => {
                event.currentTarget.style.color = "#1B1B1B";
                event.currentTarget.style.backgroundColor = PALETTE.tan;
              }}
              onClick={onOpen}
            >
              VIEW MORE
            </button>
          </motion.div>
        </div>
      </div>
    );
  };

  // For first row on mobile: auto-visible; others scroll-triggered
  const isFirst = number === 1;
  const containerMotionProps =
    isMobile && isFirst
      ? { initial: "visible", animate: "visible" }
      : { initial: "hidden", whileInView: "visible", viewport: sectionViewport };

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 items-start gap-10"
      {...containerMotionProps}
    >
      {imageSide === "left" ? (
        <>
          {/* Image: slides from left when on left */}
          <motion.div className="order-1 md:order-1" variants={fadeSlideLeft}>
            {Image}
          </motion.div>

          {/* Text: slides from right */}
          <motion.div className="order-2 md:order-2" variants={fadeSlideRight}>
            <Text side="right" />
          </motion.div>
        </>
      ) : (
        <>
          {/* Image: slides from right when on right */}
          <motion.div className="order-1 md:order-2" variants={fadeSlideRight}>
            {Image}
          </motion.div>

          {/* Text: slides from left */}
          <motion.div className="order-2 md:order-1" variants={fadeSlideLeft}>
            <Text side="left" />
          </motion.div>
        </>
      )}
    </motion.div>
  );
}
